/**
 * Hook for RCTI PDF Verification
 *
 * Handles file uploads, base64 conversion, and Cloud Function calls
 * for verifying RCTI PDF totals against BigQuery data.
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import type {
  VerificationResponse,
  VerificationResult,
  VerificationSummary,
} from '../types/rctiVerification';

interface UseRctiVerificationReturn {
  // State
  isVerifying: boolean;
  error: string | null;
  results: VerificationResult[];
  summary: VerificationSummary | null;
  verifiedAt: string | null;

  // Actions
  verifyPdfs: (
    companyId: string,
    templateId: string,
    files: File[],
    toleranceCents?: number
  ) => Promise<void>;
  clearResults: () => void;
}

/**
 * Convert a File to base64 string
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove the data:application/pdf;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to read file as base64'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function useRctiVerification(): UseRctiVerificationReturn {
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<VerificationResult[]>([]);
  const [summary, setSummary] = useState<VerificationSummary | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);

  const verifyPdfs = useCallback(
    async (
      companyId: string,
      templateId: string,
      files: File[],
      toleranceCents: number = 100
    ) => {
      if (files.length === 0) {
        setError('No PDF files provided');
        return;
      }

      setIsVerifying(true);
      setError(null);
      setResults([]);
      setSummary(null);
      setVerifiedAt(null);

      try {
        // Convert files to base64
        const uploadedPdfs = await Promise.all(
          files.map(async (file) => ({
            fileName: file.name,
            base64Content: await fileToBase64(file),
          }))
        );

        // Call Cloud Function
        const verifyRcti = httpsCallable<
          {
            companyId: string;
            templateId: string;
            verificationMode: 'uploadedBuffers';
            uploadedPdfs: { fileName: string; base64Content: string }[];
            toleranceCents: number;
          },
          VerificationResponse
        >(functions, 'verifyRctiAgainstBigQuery');

        const response = await verifyRcti({
          companyId,
          templateId,
          verificationMode: 'uploadedBuffers',
          uploadedPdfs,
          toleranceCents,
        });

        const data = response.data;

        if (!data.success && data.error) {
          setError(data.error);
          return;
        }

        setResults(data.results);
        setSummary(data.summary);
        setVerifiedAt(data.verifiedAt);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Verification failed';
        setError(errorMessage);
        console.error('[useRctiVerification] Error:', err);
      } finally {
        setIsVerifying(false);
      }
    },
    []
  );

  const clearResults = useCallback(() => {
    setResults([]);
    setSummary(null);
    setVerifiedAt(null);
    setError(null);
  }, []);

  return {
    isVerifying,
    error,
    results,
    summary,
    verifiedAt,
    verifyPdfs,
    clearResults,
  };
}
